import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthContext';
import QuickCaptureWidget from '../components/QuickCaptureWidget';
import PatientActionResponses from '../components/PatientActionResponses';
import VitalsEntryWidget from '../components/VitalsEntryWidget';
import DailyActionsWidget from '../components/DailyActionsWidget';
import api from '../api/client';
import { getSocket } from '../services/socket';
import toast from 'react-hot-toast';
import {
  Activity, Heart, Stethoscope, CheckCircle, Users, Clock, X, UserPlus, AlertCircle, ChevronRight, Mail, Phone, MessageSquare, Search, LogOut as LeaveIcon,
  Video, Home as HomeVisitIcon, Pill, CalendarClock, ExternalLink, Siren, Loader2
} from 'lucide-react';

/**
 * PatientDashboard — The patient "Today" landing page.
 * Pure content, no chrome (AppLayout wraps this).
 */
const PatientDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [recentSymptoms, setRecentSymptoms] = useState([]);
  const [moodSubmitting, setMoodSubmitting] = useState(null);
  const [moodReassurance, setMoodReassurance] = useState(null);
  const [careAction, setCareAction] = useState(null);
  const [careActions, setCareActions] = useState([]);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(
    () => sessionStorage.getItem('profileBannerDismissed') === 'true'
  );
  const [careTeam, setCareTeam] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [careTeamSearch, setCareTeamSearch] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [caregivers, setCaregivers] = useState([]);
  const [sosSending, setSosSending] = useState(false);
  const [sosConfirm, setSosConfirm] = useState(false);

  const dailyActionsRef = useRef(null);
  const moodSectionRef = useRef(null);

  const computeCompletion = (p) => {
    const checks = [
      { key: 'dob',       label: 'Date of birth',     done: !!p?.demographics?.dateOfBirth },
      { key: 'gender',    label: 'Gender',             done: !!p?.demographics?.gender },
      { key: 'language',  label: 'Language',           done: !!p?.demographics?.preferredLanguage },
      { key: 'emergency', label: 'Emergency contact',  done: !!p?.demographics?.caregiverContact?.phone },
      { key: 'vitals',    label: 'Baseline vitals',    done: !!p?.baseline?.normalVitals?.heartRate },
    ];
    const done = checks.filter(c => c.done).length;
    const missing = checks.filter(c => !c.done).map(c => c.label);
    return { pct: Math.round((done / checks.length) * 100), missing, onboardingCompleted: !!p?.onboardingCompleted };
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, symptomsRes, riskRes, requestsRes, profileRes, scheduleRes] = await Promise.all([
        api.get('/symptoms/stats/dashboard').catch(() => null),
        api.get('/symptoms/me?page=1&limit=20').catch(() => null),
        api.get('/risk-assessments/me').catch(() => null),
        api.get('/care-requests/my-requests?status=pending').catch(() => null),
        api.get('/patient-profile/me').catch(() => null),
        api.get('/care-events/my-schedule?days=14').catch(() => null),
      ]);
      if (statsRes?.data?.success) setStats(statsRes.data.data);
      const symptomsData = symptomsRes?.data?.data || symptomsRes?.data || [];
      setRecentSymptoms(Array.isArray(symptomsData) ? symptomsData : []);
      const actions = riskRes?.data?.data?.recommendedActions || [];
      // Keep up to 3 patient-facing actions ordered by priority
      const patientActions = actions.filter(a => a.patientFacing).slice(0, 3);
      const topAction = patientActions[0] || null;
      setCareAction(topAction);
      setCareActions(patientActions);
      setPendingRequests(requestsRes?.data?.stats?.pending || requestsRes?.data?.data?.length || 0);
      if (scheduleRes?.data?.success) setAppointments(scheduleRes.data.data || []);
      // Load caregivers (non-blocking, best-effort)
      api.get('/caregiver/my-caregivers').then(r => setCaregivers(r.data.data || [])).catch(() => {});

      if (profileRes?.data?.success) {
        setProfileCompletion(computeCompletion(profileRes.data.data));
        const data = profileRes.data.data;
        const team = [];
        if (data?.primaryCareProvider) {
          team.push({ ...data.primaryCareProvider, role: 'Primary Care' });
        }
        if (data?.careTeam?.length) {
          data.careTeam.forEach(ct => {
            if (ct.providerId && !team.find(t => t._id === ct.providerId._id)) {
              team.push({ ...ct.providerId, role: ct.role?.replace(/_/g, ' ') || 'Care Team' });
            }
          });
        }
        setCareTeam(team);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Refresh schedule when a new appointment is pushed via WebSocket
  useEffect(() => {
    const socket = getSocket?.();
    if (!socket) return;
    const refresh = () => api.get('/care-events/my-schedule?days=14').then(r => {
      if (r?.data?.success) setAppointments(r.data.data || []);
    }).catch(() => {});
    socket.on('appointmentScheduled', refresh);
    return () => socket.off('appointmentScheduled', refresh);
  }, []);

  const handleMoodCheckin = async (mood, severity) => {
    setMoodSubmitting(mood);
    try {
      await api.post('/care-events/mood', { mood, severity });
      dailyActionsRef.current?.markDone('mood');
      if (mood === 'good') {
        toast.success('Glad to hear that!');
      } else {
        setMoodReassurance({
          mood,
          time: new Date(),
          responseTime: mood === 'urgent' ? 'Within 1 hour' : '4 hours',
          message: mood === 'urgent'
            ? 'Your care team has been alerted and will call you within the hour. Keep your phone nearby. If this is a medical emergency, call 911.'
            : "We've noted how you're feeling. Your care team will check in with you soon.",
          nextSteps: mood === 'urgent'
            ? ['Keep your phone nearby — someone will call you', 'If you feel worse, call 911 immediately', 'Tap "Report Urgent Change" below for more detail']
            : ['Your care team will follow up within a few hours', 'Tap "Report an Issue" if anything changes']
        });
      }
      fetchData();
    } catch {
      toast.error('Failed to record mood');
    } finally {
      setMoodSubmitting(null);
    }
  };

  const triggerSOS = async () => {
    if (!sosConfirm) { setSosConfirm(true); return; }
    setSosSending(true);
    setSosConfirm(false);
    try {
      const res = await api.post('/sos', { message: 'Patient needs immediate assistance.' });
      toast.success(res.data.message || 'Emergency alert sent!', { duration: 6000 });
    } catch {
      toast.error('Failed to send alert. Call emergency services if needed.');
    } finally {
      setSosSending(false);
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const activeSymptom = recentSymptoms.find(s => s.status === 'OPEN' && !s.title?.toLowerCase().startsWith('mood check-in'));
  const latestSymptom = recentSymptoms.find(s => !s.title?.toLowerCase().startsWith('mood check-in'));

  const symptomTimeSince = (date) => {
    const ms = Date.now() - new Date(date).getTime();
    const minutes = Math.floor(ms / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const symptomSevConfig = {
    critical: { badge: 'bg-red-100 text-red-700',    label: 'Critical' },
    high:     { badge: 'bg-orange-100 text-orange-700', label: 'High'  },
    medium:   { badge: 'bg-amber-100 text-amber-700',  label: 'Medium' },
    low:      { badge: 'bg-emerald-100 text-emerald-700', label: 'Low' },
  };

  if (loading) {
    return (
      <div className="w-full space-y-6 py-6">
        {[1, 2, 3].map(i => (
          <div key={i} className="h-36 rounded-3xl animate-pulse bg-slate-100" />
        ))}
      </div>
    );
  }

  const dismissBanner = () => {
    sessionStorage.setItem('profileBannerDismissed', 'true');
    setBannerDismissed(true);
  };

  const showBanner = !bannerDismissed && profileCompletion && profileCompletion.pct < 100;

  return (
    <div className="w-full space-y-5 sm:space-y-8 py-4 sm:py-6">
      {/* Profile completion banner */}
      {showBanner && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 sm:p-5 flex items-start gap-3 sm:gap-4 relative">
          <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-amber-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between gap-4 mb-1">
              <p className="text-sm font-extrabold text-amber-900">
                Your profile is {profileCompletion.pct}% complete
              </p>
              <button onClick={dismissBanner} className="p-1 hover:bg-amber-100 rounded-lg transition-colors flex-shrink-0">
                <X className="w-4 h-4 text-amber-500" />
              </button>
            </div>
            <div className="h-1.5 bg-amber-200 rounded-full mb-2 overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full transition-all" style={{ width: `${profileCompletion.pct}%` }} />
            </div>
            <p className="text-xs text-amber-700 font-medium mb-3">
              Missing: {profileCompletion.missing.join(' · ')}
            </p>
            <button
              onClick={() => navigate('/onboarding')}
              className="flex items-center gap-2 text-xs font-extrabold text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-2 rounded-xl transition-all"
            >
              Complete profile <UserPlus className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── DAILY ACTIONS ──────────────────────────────────────── */}
      <DailyActionsWidget
        ref={dailyActionsRef}
        onMoodCheckin={() => moodSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
        onVitalsLog={() => {
          const vitalsSection = document.querySelector('[aria-label="Log vitals"]');
          vitalsSection?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }}
      />

      {/* Welcome Header */}
      <header aria-label="Welcome">
        <h1 className="font-headline text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{greeting}, {firstName}.</h1>
        <p className="text-on-surface-variant text-base sm:text-lg">Let's check in on your day.</p>
      </header>

      {/* ── EMERGENCY SOS ─────────────────────────────────────── */}
      <div className="flex items-center justify-end">
        {sosConfirm ? (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-2xl px-4 py-2.5">
            <span className="text-xs font-bold text-red-700">Confirm — alert your doctor and caregivers?</span>
            <button
              onClick={triggerSOS}
              disabled={sosSending}
              className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
            >
              {sosSending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Siren className="w-3 h-3" />}
              Yes, send
            </button>
            <button onClick={() => setSosConfirm(false)} className="text-xs text-slate-400 hover:text-slate-600 px-1">Cancel</button>
          </div>
        ) : (
          <button
            onClick={triggerSOS}
            disabled={sosSending}
            className="flex items-center gap-1.5 px-4 py-2 bg-red-50 border border-red-200 text-red-600 text-xs font-black rounded-2xl hover:bg-red-100 hover:border-red-300 transition-all disabled:opacity-50"
          >
            <Siren className="w-3.5 h-3.5" />
            Emergency SOS
          </button>
        )}
      </div>

      {/* ── PRIMARY ACTION: How are you feeling? ─────────────── */}
      <section aria-label="Mood check-in" ref={moodSectionRef}>
        <h3 className="font-headline text-lg font-bold tracking-tight text-slate-900 mb-3" id="mood-heading">How are you feeling right now?</h3>
        <div className="grid grid-cols-3 gap-3" role="group" aria-labelledby="mood-heading">
          {[
            { emoji: '😊', label: 'Good',      mood: 'good',      severity: 'low'    },
            { emoji: '😐', label: 'Not Great', mood: 'not_great', severity: 'medium' },
            { emoji: '🆘', label: 'Need Help', mood: 'urgent',    severity: 'urgent' },
          ].map((item) => (
            <button
              key={item.label}
              disabled={moodSubmitting === item.mood}
              onClick={() => handleMoodCheckin(item.mood, item.severity)}
              aria-label={`Report mood: ${item.label}${item.mood === 'urgent' ? ' — your care team will be notified' : ''}`}
              aria-busy={moodSubmitting === item.mood}
              className={`flex flex-col items-center justify-center py-5 px-3 rounded-2xl transition-all active:scale-95 group border-2 disabled:opacity-50 shadow-sm ${
                item.mood === 'urgent'
                  ? 'bg-red-50 border-red-200 hover:border-red-400'
                  : 'bg-white border-slate-100 hover:border-primary/30 hover:shadow-md'
              }`}
            >
              <span className="text-4xl mb-2 group-hover:scale-110 transition-transform" aria-hidden="true">{item.emoji}</span>
              <span className={`font-bold text-sm text-center leading-tight ${item.mood === 'urgent' ? 'text-red-600' : 'text-slate-700'}`}>
                {moodSubmitting === item.mood ? 'Saving…' : item.label}
              </span>
              {item.mood === 'urgent' && (
                <span className="text-[10px] text-red-500 font-medium text-center leading-tight mt-1">Care team notified</span>
              )}
            </button>
          ))}
        </div>
      </section>

      {/* Mood reassurance — immediate inline feedback after tapping */}
      {moodReassurance && (
        <div
          className={`border rounded-2xl p-4 sm:p-5 relative ${moodReassurance.mood === 'urgent' ? 'bg-danger-50 border-danger-200' : 'bg-emerald-50 border-emerald-200'}`}
          role="status"
          aria-live="polite"
        >
          <button onClick={() => setMoodReassurance(null)} className="absolute top-3 right-3 p-1 hover:bg-white/50 rounded-full" aria-label="Dismiss confirmation">
            <X className={`w-4 h-4 ${moodReassurance.mood === 'urgent' ? 'text-danger-400' : 'text-emerald-400'}`} />
          </button>
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${moodReassurance.mood === 'urgent' ? 'bg-danger-100' : 'bg-emerald-100'}`}>
              {moodReassurance.mood === 'urgent'
                ? <AlertCircle className="w-5 h-5 text-danger-600" aria-hidden="true" />
                : <CheckCircle className="w-5 h-5 text-emerald-600" aria-hidden="true" />
              }
            </div>
            <div className="flex-1">
              <h4 className={`font-bold text-sm ${moodReassurance.mood === 'urgent' ? 'text-danger-900' : 'text-emerald-900'}`}>
                {moodReassurance.mood === 'urgent' ? 'Care team alerted' : 'We hear you'}
              </h4>
              <p className={`text-sm mt-1 ${moodReassurance.mood === 'urgent' ? 'text-danger-700' : 'text-emerald-700'}`}>{moodReassurance.message}</p>
              {moodReassurance.nextSteps && (
                <ul className="mt-2 space-y-1">
                  {moodReassurance.nextSteps.map((step, i) => (
                    <li key={i} className={`flex items-start gap-1.5 text-xs font-medium ${moodReassurance.mood === 'urgent' ? 'text-danger-700' : 'text-emerald-700'}`}>
                      <ChevronRight className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      {step}
                    </li>
                  ))}
                </ul>
              )}
              <div className={`flex items-center gap-1.5 mt-2 text-xs font-bold ${moodReassurance.mood === 'urgent' ? 'text-danger-600' : 'text-emerald-600'}`}>
                <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                Logged at {moodReassurance.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} — response {moodReassurance.responseTime}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Issue tiles — full inline flow, hidden once reassurance is active */}
      {!moodReassurance && (
        <section aria-label="Report an issue">
          <QuickCaptureWidget onEventCreated={fetchData} embedded />
        </section>
      )}

      {/* Quick Stats — at-a-glance orientation before the action card */}
      {stats && (
        <section aria-label="Health summary">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Your health at a glance</p>
          <div className="grid grid-cols-3 gap-2 sm:gap-4" role="group">
            <div className="bg-white rounded-2xl p-3 sm:p-4 text-center shadow-premium border border-outline-variant/10">
              <p className="text-xl sm:text-2xl font-extrabold text-primary tracking-tighter">{stats.activeSymptoms || 0}</p>
              <p className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Active</p>
            </div>
            <div className="bg-white rounded-2xl p-3 sm:p-4 text-center shadow-premium border border-outline-variant/10">
              <p className="text-xl sm:text-2xl font-extrabold text-secondary tracking-tighter">{stats.resolvedCases || 0}</p>
              <p className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Resolved</p>
            </div>
            <div className="bg-white rounded-2xl p-3 sm:p-4 text-center shadow-premium border border-outline-variant/10">
              <p className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tighter">{stats.consultingDoctors || 0}</p>
              <p className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">Care Team</p>
            </div>
          </div>
        </section>
      )}

      {/* Current Plan Card — shows next care action from the Action Engine */}
      <section aria-label="Current care plan">
        <div className="bg-white rounded-2xl p-5 sm:p-7 shadow-premium border border-outline-variant/10 relative overflow-hidden">
          <div className="flex justify-between items-center mb-4 sm:mb-5">
            <span className="bg-secondary-container text-on-secondary-container px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest">
              {careAction ? 'Next Step' : 'Care Plan'}
            </span>
            {careAction?.patientFacing?.responseTime && (
              <div className="flex items-center gap-1.5 text-secondary">
                <Clock className="w-4 h-4" />
                <span className="font-bold text-xs">{careAction.patientFacing.responseTime}</span>
              </div>
            )}
            {!careAction && activeSymptom && (
              <div className="flex items-center gap-1.5 text-amber-600">
                <Activity className="w-4 h-4" />
                <span className="font-bold text-xs">Active report</span>
              </div>
            )}
          </div>

          {careAction ? (
            <>
              <h2 className="font-headline text-xl sm:text-2xl font-extrabold text-primary mb-2">
                {careAction.patientFacing.title}
              </h2>
              <p className="text-on-surface-variant mb-4 sm:mb-5 leading-relaxed font-medium text-sm sm:text-base">
                {careAction.patientFacing.message}
              </p>

              {careAction.patientFacing.guidance && (
                <ul className="mb-4 sm:mb-5 space-y-2">
                  {careAction.patientFacing.guidance.map((tip, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <CheckCircle className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              )}

              {/* Additional pending actions */}
              {careActions.length > 1 && (
                <div className="mb-4 space-y-2">
                  {careActions.slice(1).map((action, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                      <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-amber-800">{action.patientFacing.title}</p>
                        <p className="text-xs text-amber-700 mt-0.5">{action.patientFacing.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {careAction.patientFacing.actionOptions ? (
                <div className="space-y-2">
                  {careAction.patientFacing.actionOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => opt.url ? navigate(opt.url) : navigate('/my-health')}
                      className="w-full py-3 sm:py-3.5 bg-primary/10 text-primary rounded-2xl font-bold text-sm hover:bg-primary/20 active:scale-[0.98] transition-all"
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              ) : (
                <button
                  onClick={() => navigate('/my-health')}
                  className="w-full py-3.5 sm:py-4 bg-primary text-white rounded-2xl font-bold shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
                >
                  View My Health
                </button>
              )}

              {/* Escalation path — what to do if worse before callback */}
              {careAction.patientFacing.responseTime && (
                <div className="mt-4 p-3 bg-surface-50 border border-outline-variant/10 rounded-xl">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    If you feel worse before your care team contacts you
                  </p>
                  <button
                    onClick={() => navigate('/report')}
                    className="w-full py-2.5 border-2 border-danger-200 text-danger-700 bg-danger-50 rounded-xl font-semibold text-sm hover:bg-danger-100 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" />
                    Report Urgent Change
                  </button>
                </div>
              )}
            </>
          ) : activeSymptom ? (
            <>
              <h2 className="font-headline text-xl sm:text-2xl font-extrabold text-primary mb-2">Active Report</h2>
              <p className="text-on-surface-variant mb-4 sm:mb-5 leading-relaxed font-medium text-sm sm:text-base">
                Your care team is monitoring your open report. Keep it updated with any changes.
              </p>
              <button
                onClick={() => navigate(`/symptom/${activeSymptom._id}`)}
                className="w-full py-3.5 sm:py-4 bg-primary text-white rounded-2xl font-bold shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
              >
                View Report
              </button>
            </>
          ) : (
            <>
              <h2 className="font-headline text-xl sm:text-2xl font-extrabold text-primary mb-2">All clear</h2>
              <p className="text-on-surface-variant mb-4 sm:mb-5 leading-relaxed font-medium text-sm sm:text-base">
                You have no active health issues. Let us know if anything changes.
              </p>
              <button
                onClick={() => navigate('/report')}
                className="w-full py-3.5 sm:py-4 bg-primary text-white rounded-2xl font-bold shadow-md hover:shadow-lg active:scale-[0.98] transition-all"
              >
                Report an Issue
              </button>
            </>
          )}
        </div>
      </section>

      {/* Pending Care Requests */}
      {pendingRequests > 0 && (
        <section aria-label="Pending care requests">
          <button
            onClick={() => navigate('/care-requests')}
            className="w-full bg-primary/5 border border-primary/20 rounded-2xl p-5 flex items-center gap-4 hover:bg-primary/10 active:scale-[0.98] transition-all text-left"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 relative">
              <UserPlus className="w-6 h-6 text-primary" />
              <span className="absolute -top-1 -right-1 bg-primary text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                {pendingRequests}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-sm text-slate-900">
                {pendingRequests} Care Team Request{pendingRequests !== 1 ? 's' : ''}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">A doctor wants to join your care team — tap to review</p>
            </div>
          </button>
        </section>
      )}

      {/* ── Upcoming Appointments ──────────────────────────── */}
      {appointments.length > 0 && (() => {
        const APPT_CONFIG = {
          virtual_visit:      { label: 'Virtual Visit',        Icon: Video,          bg: 'bg-blue-50',   border: 'border-blue-100',   badge: 'bg-blue-100 text-blue-700',   icon: 'text-blue-600' },
          home_visit:         { label: 'Home Visit',           Icon: HomeVisitIcon,  bg: 'bg-teal-50',   border: 'border-teal-100',   badge: 'bg-teal-100 text-teal-700',   icon: 'text-teal-600' },
          pharmacist_consult: { label: 'Pharmacist Consult',   Icon: Pill,           bg: 'bg-purple-50', border: 'border-purple-100', badge: 'bg-purple-100 text-purple-700', icon: 'text-purple-600' },
        };
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);

        return (
          <section aria-label="Upcoming appointments">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-headline text-lg sm:text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
                <CalendarClock className="w-5 h-5 text-primary" />
                Upcoming
              </h3>
            </div>
            <div className="space-y-3">
              {appointments.map((appt) => {
                const cfg = APPT_CONFIG[appt.appointmentData?.appointmentType] || APPT_CONFIG.virtual_visit;
                const { Icon } = cfg;
                const scheduled = appt.appointmentData?.scheduledDate ? new Date(appt.appointmentData.scheduledDate) : null;
                const apptDay = scheduled ? new Date(scheduled) : null;
                if (apptDay) apptDay.setHours(0, 0, 0, 0);
                const dayLabel = apptDay
                  ? apptDay.getTime() === today.getTime() ? 'Today'
                  : apptDay.getTime() === tomorrow.getTime() ? 'Tomorrow'
                  : scheduled.toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })
                  : null;
                const timeLabel = scheduled ? scheduled.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null;
                const meetingLink = appt.appointmentData?.meetingLink;
                const isToday = apptDay?.getTime() === today.getTime();

                return (
                  <div
                    key={appt._id}
                    className={`${cfg.bg} border ${cfg.border} rounded-2xl p-4 flex items-center gap-4`}
                  >
                    <div className={`w-11 h-11 rounded-xl bg-white flex items-center justify-center shrink-0 shadow-sm`}>
                      <Icon className={`w-5 h-5 ${cfg.icon}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${cfg.badge}`}>
                          {cfg.label}
                        </span>
                        {isToday && (
                          <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            Today
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-900 mt-1">
                        {dayLabel}{timeLabel ? ` at ${timeLabel}` : ''}
                      </p>
                      {appt.appointmentData?.notes && (
                        <p className="text-xs text-slate-500 mt-0.5 truncate">{appt.appointmentData.notes}</p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      {meetingLink ? (
                        <a
                          href={meetingLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-xl text-xs font-bold hover:bg-blue-700 transition-colors"
                        >
                          <Video className="w-3.5 h-3.5" /> Join
                        </a>
                      ) : (
                        <button
                          onClick={() => navigate(`/care-event/${appt._id}`)}
                          className="flex items-center gap-1.5 px-3 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-xs font-bold hover:bg-slate-50 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> View
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })()}

      {/* Pending Actions from Care Team */}
      <PatientActionResponses />

      {/* Latest Symptom Report */}
      {latestSymptom && (() => {
        const sev = symptomSevConfig[latestSymptom.severity] || symptomSevConfig.medium;
        const isOpen = latestSymptom.status === 'OPEN';
        const nonMoodCount = recentSymptoms.filter(s => !s.title?.toLowerCase().startsWith('mood check-in')).length;
        return (
          <section aria-label="Latest symptom report">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-headline text-lg sm:text-xl font-bold tracking-tight text-slate-900">Latest Report</h3>
              {nonMoodCount > 1 && (
                <button
                  onClick={() => navigate('/my-health')}
                  className="flex items-center gap-1 text-xs font-extrabold text-primary hover:text-primary/80 transition-colors"
                >
                  View all {nonMoodCount} <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <button
              onClick={() => navigate(`/symptom/${latestSymptom._id}`)}
              className="w-full bg-white rounded-2xl p-5 border border-outline-variant/10 shadow-sm hover:shadow-md active:scale-[0.99] transition-all text-left"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${isOpen ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isOpen ? 'bg-amber-500 animate-pulse' : 'bg-slate-400'}`} />
                      {isOpen ? 'Open' : 'Resolved'}
                    </span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${sev.badge}`}>
                      {sev.label}
                    </span>
                  </div>
                  <p className="font-bold text-slate-900 text-base truncate">{latestSymptom.title || 'Health report'}</p>
                  {latestSymptom.description && (
                    <p className="text-sm text-slate-500 mt-1 line-clamp-2 font-medium leading-relaxed">{latestSymptom.description}</p>
                  )}
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-400 font-medium">
                    <Clock className="w-3.5 h-3.5" />
                    Reported {symptomTimeSince(latestSymptom.createdAt)}
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
              </div>
            </button>
          </section>
        );
      })()}


      {/* Vitals Entry (device integration pipeline — thresholds + risk assessment) */}
      <VitalsEntryWidget
        onSuccess={() => dailyActionsRef.current?.markDone('vitals')}
      />

      {/* Caregivers */}
      <section aria-label="Family caregivers">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-headline text-lg sm:text-xl font-bold tracking-tight text-slate-900">Family Caregivers</h3>
          <button
            onClick={() => navigate('/my-data')}
            className="flex items-center gap-1 text-xs font-extrabold text-primary hover:text-primary/80 transition-colors"
          >
            Manage <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {caregivers.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 p-5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center">
                <Heart className="w-5 h-5 text-slate-300" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-700">No caregivers linked</p>
                <p className="text-xs text-slate-400">Invite a family member to keep them informed</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/my-data')}
              className="text-xs font-bold text-primary hover:underline shrink-0"
            >
              Invite
            </button>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100">
            {caregivers.slice(0, 3).map(cg => (
              <div key={cg._id} className="flex items-center gap-3 px-4 py-3">
                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                  <Heart className="w-4 h-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800 truncate">{cg.caregiverName}</p>
                  <p className="text-xs text-slate-400 capitalize">{cg.relationship} · <span className={cg.status === 'active' ? 'text-emerald-600' : 'text-amber-500'}>{cg.status}</span></p>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Care Team */}
      <section aria-label="Your care team">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-headline text-lg sm:text-xl font-bold tracking-tight text-slate-900">Your Care Team</h3>
          <button
            onClick={() => navigate('/care-team')}
            className="flex items-center gap-1 text-xs font-extrabold text-primary hover:text-primary/80 transition-colors"
          >
            Manage <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {careTeam.length > 0 && (
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={careTeamSearch}
              onChange={e => setCareTeamSearch(e.target.value)}
              placeholder="Search by name or speciality…"
              className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all outline-none"
            />
            {careTeamSearch && (
              <button
                onClick={() => setCareTeamSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 hover:bg-slate-200 rounded-full transition-colors"
              >
                <X className="w-3 h-3 text-slate-400" />
              </button>
            )}
          </div>
        )}

        {careTeam.length > 0 ? (() => {
          const filtered = careTeam.filter(d => {
            const q = careTeamSearch.toLowerCase();
            return !q ||
              d.name?.toLowerCase().includes(q) ||
              d.specialization?.toLowerCase().includes(q) ||
              d.role?.toLowerCase().includes(q);
          });
          return filtered.length > 0 ? (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
            {filtered.map((doctor, i) => {
              const initials = doctor.name
                ? doctor.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
                : '?';
              const palettes = [
                { bg: 'bg-primary/10',    text: 'text-primary',        badge: 'bg-primary/5 text-primary/70' },
                { bg: 'bg-secondary/10',  text: 'text-secondary',      badge: 'bg-secondary/5 text-secondary/70' },
                { bg: 'bg-emerald-100',   text: 'text-emerald-700',    badge: 'bg-emerald-50 text-emerald-600' },
                { bg: 'bg-violet-100',    text: 'text-violet-700',     badge: 'bg-violet-50 text-violet-600' },
                { bg: 'bg-amber-100',     text: 'text-amber-700',      badge: 'bg-amber-50 text-amber-600' },
              ];
              const pal = palettes[i % palettes.length];
              return (
                <div
                  key={doctor._id || i}
                  className="flex-shrink-0 bg-white rounded-2xl p-4 sm:p-5 border border-slate-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer active:scale-[0.97]"
                  style={{ width: 'calc(50% - 6px)', minWidth: '140px', maxWidth: '220px' }}
                  onClick={() => setSelectedDoctor({ ...doctor, paletteIdx: i })}
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-extrabold text-base mb-3 ${pal.bg} ${pal.text}`}>
                    {initials}
                  </div>
                  <p className="text-sm font-extrabold text-slate-900 leading-tight truncate">{doctor.name || 'Doctor'}</p>
                  <p className="text-xs text-slate-400 font-medium mt-0.5 truncate">{doctor.specialization || 'Physician'}</p>
                  <span className={`inline-block mt-3 text-[10px] font-bold px-2 py-0.5 rounded-full capitalize ${pal.badge}`}>
                    {doctor.role}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-6">
            <Search className="w-5 h-5 text-slate-300" />
            <p className="text-sm font-bold text-slate-400">No results for "{careTeamSearch}"</p>
            <button onClick={() => setCareTeamSearch('')} className="text-xs font-bold text-primary hover:underline">Clear</button>
          </div>
        );
        })() : (
          <div className="w-full bg-slate-50 border border-slate-100 rounded-2xl p-6 flex flex-col items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary/5 flex items-center justify-center">
              <Users className="w-6 h-6 text-primary/60" />
            </div>
            <div className="text-center">
              <p className="text-sm font-extrabold text-slate-700">No care team yet</p>
              <p className="text-xs text-slate-400 mt-0.5 max-w-[220px] leading-relaxed">Your care team will appear here once a doctor sends you a care request</p>
            </div>
          </div>
        )}
      </section>

      {/* Doctor profile modal */}
      {selectedDoctor && (() => {
        const i = selectedDoctor.paletteIdx ?? 0;
        const palettes = [
          { bg: 'bg-primary/10',   text: 'text-primary' },
          { bg: 'bg-secondary/10', text: 'text-secondary' },
          { bg: 'bg-emerald-100',  text: 'text-emerald-700' },
          { bg: 'bg-violet-100',   text: 'text-violet-700' },
          { bg: 'bg-amber-100',    text: 'text-amber-700' },
        ];
        const pal = palettes[i % palettes.length];
        const initials = selectedDoctor.name
          ? selectedDoctor.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
          : '?';
        return (
          <div
            className="fixed inset-0 z-[200] flex items-end justify-center bg-black/40 backdrop-blur-sm"
            onClick={() => setSelectedDoctor(null)}
          >
            <div
              className="relative w-full max-w-md bg-white rounded-t-3xl p-5 sm:p-6 pb-8 shadow-2xl max-h-[85dvh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              {/* Handle */}
              <div className="w-10 h-1 bg-slate-200 rounded-full mx-auto mb-6" />
              {/* Header */}
              <div className="flex items-center gap-4 mb-5">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center font-extrabold text-xl flex-shrink-0 ${pal.bg} ${pal.text}`}>
                  {initials}
                </div>
                <div className="min-w-0">
                  <h3 className="font-headline text-lg font-extrabold text-slate-900 truncate">{selectedDoctor.name || 'Doctor'}</h3>
                  <p className="text-sm text-slate-500 font-medium">{selectedDoctor.specialization || 'Physician'}</p>
                  <span className="inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 capitalize">
                    {selectedDoctor.role}
                  </span>
                </div>
              </div>
              {/* Contact rows */}
              <div className="space-y-3 mb-6">
                {selectedDoctor.email && (
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-slate-400" />
                    </div>
                    <span className="truncate font-medium">{selectedDoctor.email}</span>
                  </div>
                )}
                {selectedDoctor.phone && (
                  <div className="flex items-center gap-3 text-sm text-slate-700">
                    <div className="w-9 h-9 rounded-xl bg-slate-50 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-4 h-4 text-slate-400" />
                    </div>
                    <span className="font-medium">{selectedDoctor.phone}</span>
                  </div>
                )}
              </div>
              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    const doctorId = selectedDoctor._id || selectedDoctor.id;
                    const doctorName = selectedDoctor.name;
                    setSelectedDoctor(null);
                    try {
                      const res = await api.get('/symptoms/me?page=1&limit=50').catch(() => null);
                      const symptoms = res?.data?.data || [];
                      const matches = symptoms.filter(s =>
                        s.responses?.some(r => r.doctorId === doctorId || r.doctorId?._id === doctorId)
                      );
                      if (matches.length === 1) {
                        navigate(`/chat/${matches[0]._id}`);
                      } else if (matches.length > 1) {
                        navigate('/messages');
                      } else {
                        toast('Report a health issue first to start a conversation.', { icon: 'ℹ️' });
                        navigate('/report');
                      }
                    } catch { navigate('/messages'); }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-primary/10 text-primary rounded-xl text-xs font-bold hover:bg-primary/20 transition-colors"
                >
                  <MessageSquare className="w-4 h-4" /> Message
                </button>
                <button
                  onClick={async () => {
                    const doctorId = selectedDoctor._id || selectedDoctor.id;
                    const doctorName = selectedDoctor.name;
                    if (!confirm(`Remove ${doctorName} from your care team? This will revoke their access to your data.`)) return;
                    setSelectedDoctor(null);
                    try {
                      await api.post(`/patient-profile/leave-doctor/${doctorId}`);
                      toast.success(`${doctorName} removed from your care team`);
                      setCareTeam(prev => prev.filter(d => (d._id || d.id) !== doctorId));
                    } catch { toast.error('Failed to remove doctor'); }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl text-xs font-bold hover:bg-red-100 transition-colors"
                >
                  <LeaveIcon className="w-4 h-4" /> Remove from Care Team
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </div>
  );
};

export default PatientDashboard;
